import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useFilters
 * Per-column filter sets + the popup-open state.  Exposes
 * `isRowVisible(rowIndex)` which combines filters with any externally
 * hidden rows passed in (so the grid only ever consults one predicate).
 *
 * Filter VISIBILITY follows Excel: the column dropdown is shown only on
 * columns covered by the file's autoFilter range.  A toolbar toggle
 * (`toggleFilterEnabled`) turns filtering on for the whole data region
 * (or off again), exactly like Excel's Data ▸ Filter button.  When whole
 * COLUMNS are selected, the toggle scopes the filter to just those
 * columns (QA #13).
 *
 * Row visibility is SNAPSHOTTED when a filter is applied (Excel hides a
 * fixed set of rows).  Editing a filtered cell afterwards therefore does
 * NOT re-run the filter and vanish the row being edited (QA #12); the
 * snapshot is refreshed only when filters change or the sort order
 * reorders the rows.
 *
 * On a sheet's first visit, filters are seeded from the criteria saved in
 * the workbook's own <autoFilter> (QA #6) — so a file sent with an active
 * filter opens filtered, like Excel.
 *
 * Filters are remembered per sheet — switching tabs doesn't lose them.
 */
export default function useFilters({
  activeSheet, activeModel, normalizedRows, hiddenRows,
  selectionType, normalizedSelection, sortSignature,
}) {
  const [filters, setFilters] = useState({});
  const [activeFilterCol, setActiveFilterCol] = useState(null);
  // null = follow the file; otherwise an explicit user override.
  const [filterOverride, setFilterOverride] = useState(null); // null | false | {c1,c2}
  // Snapshot of the rows hidden by the current filters (see header note).
  const [filteredOutRows, setFilteredOutRows] = useState(() => new Set());

  // Live rows for snapshot computation (stable callback identity).
  const rowsRef = useRef(normalizedRows);
  rowsRef.current = normalizedRows;

  // Evaluate `flt` against the CURRENT rows once, returning the hidden set.
  const computeHidden = useCallback((flt) => {
    const rows = rowsRef.current || [];
    const out = new Set();
    const cols = Object.keys(flt || {});
    if (cols.length === 0) return out;
    for (let r = 1; r < rows.length; r += 1) {
      for (let i = 0; i < cols.length; i += 1) {
        const allowed = flt[cols[i]];
        if (allowed && allowed.size > 0) {
          const v = rows[r] ? rows[r][cols[i]] : null;
          const strVal = v == null || v === "" ? "(Blank)" : String(v);
          if (!allowed.has(strVal)) { out.add(r); break; }
        }
      }
    }
    return out;
  }, []);

  // Per-sheet filter memory: each sheet keeps its own filters + on/off
  // override + hidden-row snapshot (like Excel — switching tabs doesn't
  // lose a sheet's filtering).
  const bySheetRef = useRef({});            // { [sheet]: { filters, override, hidden } }
  const prevSheetRef = useRef(null);
  useEffect(() => {
    // Save the OUTGOING sheet's state (filters/override here still hold the
    // previous sheet's values — they update only when we restore below).
    if (prevSheetRef.current && prevSheetRef.current !== activeSheet) {
      bySheetRef.current[prevSheetRef.current] = {
        filters, override: filterOverride, hidden: filteredOutRows,
      };
    }
    const saved = bySheetRef.current[activeSheet];
    if (saved) {
      // Restore the sheet's state exactly as the user left it.
      setFilters(saved.filters);
      setFilterOverride(saved.override);
      setFilteredOutRows(saved.hidden || new Set());
    } else {
      // FIRST visit: seed from the filter criteria saved in the file's own
      // autoFilter, so a workbook sent with an active filter opens filtered.
      const crit = activeModel && activeModel.autoFilterCriteria;
      if (crit && Object.keys(crit).length > 0) {
        const seeded = {};
        Object.keys(crit).forEach((c) => { seeded[c] = new Set(crit[c]); });
        setFilters(seeded);
        setFilterOverride(null);
        setFilteredOutRows(computeHidden(seeded));
      } else {
        setFilters({});
        setFilterOverride(null);
        setFilteredOutRows(new Set());
      }
    }
    setActiveFilterCol(null);              // never carry an open popup across sheets
    prevSheetRef.current = activeSheet;
  }, [activeSheet]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the SORT ORDER changes, the same physical rows sit at new indices —
  // refresh the snapshot against the reordered rows (values didn't change,
  // so the same rows end up hidden, just at their new positions).
  const sortSigRef = useRef(sortSignature);
  useEffect(() => {
    if (sortSigRef.current === sortSignature) return;
    sortSigRef.current = sortSignature;
    setFilteredOutRows(computeHidden(filters));
  }, [sortSignature, filters, computeHidden]);

  // Effective filter range: the user override if set, else the file's
  // autoFilter columns.  `filterEnabled` is true when any range applies.
  const fileCols = activeModel?.autoFilterCols || null;
  const filterRange = filterOverride === false ? null
    : (filterOverride || fileCols);
  const filterEnabled = !!filterRange;

  const isFilterColumn = useCallback(
    (colIndex) => !!filterRange && colIndex >= filterRange.c1 && colIndex <= filterRange.c2,
    [filterRange],
  );

  const toggleFilterEnabled = useCallback(() => {
    const showing = filterOverride === false ? false : (filterOverride || fileCols);
    if (showing) {
      // Turning filtering off clears any active filters so no rows stay
      // hidden once the dropdowns disappear (Excel does the same).
      setFilters({});
      setFilteredOutRows(new Set());
      setFilterOverride(false);
    } else if (selectionType === "col" && normalizedSelection) {
      // Whole column(s) selected → scope the filter to just those columns,
      // like Excel's Data ▸ Filter on a column selection (QA #13).
      setFilterOverride({ c1: normalizedSelection.c1, c2: normalizedSelection.c2 });
    } else {
      const lastCol = Math.max(0, (activeModel?.dataColCount || 1) - 1);
      setFilterOverride({ c1: 0, c2: lastCol });
    }
    setActiveFilterCol(null);
  }, [filterOverride, fileCols, activeModel?.dataColCount, selectionType, normalizedSelection]);

  // NOTE: outside-click close now lives in FilterMenu itself (DOM-containment
  // check), because the popup is portalled to <body> and a window-level click
  // listener here can't distinguish inside vs. outside via event bubbling.

  const toggleFilterMenu = useCallback((colIndex, event) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setActiveFilterCol((prev) => (prev === colIndex ? null : colIndex));
  }, []);

  const applyFilter = useCallback((colIndex, selectedValues) => {
    const next = { ...filters };
    if (!selectedValues) delete next[colIndex];
    else next[colIndex] = selectedValues;
    setFilters(next);
    // Snapshot NOW — later cell edits must not re-run the filter (QA #12).
    setFilteredOutRows(computeHidden(next));
  }, [filters, computeHidden]);

  const isRowVisible = useCallback((rowIndex) => {
    // Row 0 is always visible — it's the header row that the freeze
    // pane and filter UI hang off.
    if (rowIndex === 0) return true;
    if (hiddenRows.has(rowIndex)) return false;
    return !filteredOutRows.has(rowIndex);
  }, [hiddenRows, filteredOutRows]);

  return {
    filters, activeFilterCol, setActiveFilterCol,
    toggleFilterMenu, applyFilter, isRowVisible,
    filterEnabled, isFilterColumn, toggleFilterEnabled,
  };
}
